import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Empty } from 'antd';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  type ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import { useLineageFilter } from '@/shared/hooks/useLineageFilter';
import { useResizeObserver } from '@/shared/hooks/useResizeObserver';
import type { LineageNodeData } from '@/shared/types/lineageNode';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { HierarchyFilter } from '@/shared/ui/lineage/HierarchyFilter';
import { LineageControls } from '@/shared/ui/lineage/LineageControls';
import { LineageNode } from '@/shared/ui/lineage/LineageNode';
import { LineageNodeDrawer } from '@/shared/ui/lineage/LineageNodeDrawer';

import { useLineageNodeDrawer } from '@/features/asset-detail';
import type { LineageResponse } from '@/features/asset-detail/api/lineageApi';
import { useLineageData } from '@/features/asset-detail/model/useLineageData';

import styles from './LineageTab.module.css';

import 'reactflow/dist/style.css';

type LineageTabProps = {
  lineageData?: LineageResponse;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
};

const LINEAGE_CONTAINER_MIN_HEIGHT = 260;
const LINEAGE_CONTAINER_HEADER_OFFSET = 140;
const LINEAGE_CONTAINER_FULLSCREEN_HEIGHT_OFFSET = 80;

const LineageTabComponent: React.FC<LineageTabProps> = ({
  lineageData,
  isFullscreen = false,
  onToggleFullscreen,
  isLoading = false,
  isError = false,
  error = null,
  onRetry,
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const lineageContainerHeightOffset = useMemo(() => {
    return isFullscreen ? LINEAGE_CONTAINER_FULLSCREEN_HEIGHT_OFFSET : LINEAGE_CONTAINER_HEADER_OFFSET;
  }, [isFullscreen]);

  const { containerRef, containerHeight } = useResizeObserver({
    minHeight: LINEAGE_CONTAINER_MIN_HEIGHT,
    headerOffset: lineageContainerHeightOffset,
  });
  const { filterState, handleHierarchiesChange } = useLineageFilter();
  const { availableHierarchies, initialNodes, initialEdges } = useLineageData(lineageData, filterState);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    return nodes.find((n) => n.id === selectedNodeId)?.data || null;
  }, [selectedNodeId, nodes]);

  const { infoRows, sections } = useLineageNodeDrawer(selectedNode, isDrawerOpen);

  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [isInitialFitDone, setIsInitialFitDone] = useState(false);

  const handleNodeSelect = useCallback((node: LineageNodeData) => {
    setSelectedNodeId(node.fullyQualifiedName);
    setIsDrawerOpen(true);
  }, []);

  const nodeTypes = useMemo(
    () => ({
      lineageNode: (props: { data: LineageNodeData }) => (
        <LineageNode
          {...props}
          selected={selectedNodeId === props.data.fullyQualifiedName}
          onSelect={handleNodeSelect}
        />
      ),
    }),
    [selectedNodeId, handleNodeSelect]
  );

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setIsInitialFitDone(false);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  useEffect(() => {
    if (reactFlowInstance && nodes.length > 0 && !isInitialFitDone) {
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          duration: 200,
          minZoom: 0.5,
          maxZoom: 1.0,
        });
        setIsInitialFitDone(true);
      }, 50);
    }
  }, [reactFlowInstance, nodes, isInitialFitDone]);

  const handleToggleFullscreen = useCallback(() => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
    }
  }, [onToggleFullscreen]);

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="계보 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  if (isLoading) {
    return (
      <div className={styles.lineageTab}>
        <div className={styles.emptyState}>
          <Empty description="계보 정보를 불러오는 중..." />
        </div>
      </div>
    );
  }

  if (!initialNodes || initialNodes.length === 0) {
    return (
      <div className={styles.lineageTab}>
        <div className={styles.emptyState}>
          <Empty description="계보 정보가 없습니다" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.lineageTab} ref={containerRef}>
      <LineageControls isFullscreen={isFullscreen} onToggleFullscreen={handleToggleFullscreen} />
      <div className={styles.flowContainer} style={{ height: `${containerHeight}px` }}>
        {nodes.length === 0 ? (
          <div className={styles.emptyState}>
            <Empty description="선택한 필터에 해당하는 계보 정보가 없습니다" />
          </div>
        ) : (
          <>
            <ReactFlow
              className="touch-flow"
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              onInit={setReactFlowInstance}
              minZoom={0.05}
              maxZoom={2}
              defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
              autoPanOnNodeDrag={true}
              panOnDrag={true}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={false}
              preventScrolling={true}
              proOptions={{ hideAttribution: true }}>
              <Background />
              <Controls position="top-right" />
              <MiniMap nodeColor="#94a3b8" position="bottom-right" />
            </ReactFlow>
            {selectedNode && (
              <LineageNodeDrawer
                open={isDrawerOpen}
                onClose={() => {
                  setIsDrawerOpen(false);
                  setSelectedNodeId(null);
                }}
                data={selectedNode}
                infoRows={infoRows}
                sections={sections}
              />
            )}
          </>
        )}
        <HierarchyFilter
          availableHierarchies={availableHierarchies}
          selectedHierarchies={filterState.viewOptions.selectedHierarchies}
          onHierarchiesChange={handleHierarchiesChange}
        />
      </div>
    </div>
  );
};

export const LineageTab = React.memo(LineageTabComponent);
